/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Typography, Alert } from 'antd';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useAppDispatch } from '@/hooks/useRedux';
import { FiArrowLeft } from 'react-icons/fi';

import {
  postPasswordIntroduction,
  postPasswordValidation,
  postPasswordConfirmation,
} from '@/redux/auth/passwordManagement/thunk';

import ThemedButton from '@/components/common/buttons/Button';
import Logo from '@/components/layout/Logo';
import AuthFooter from '../AuthFooter';
import { getProfessionalErrorMessage } from '@/utils/errorHandling';

const { Title, Text } = Typography;

type Stage = 'intro' | 'confirmation' | 'success';
type FieldErrorMap = Record<string, string[]>;

const HARD_CODED_INTRO = {
  nationality: 'KE',
  is_alien: false,
  is_refugee: false,
};

const RESEND_COOLDOWN = 90;

const labelClass =
  'block text-[15px] sm:text-[16px] font-semibold text-[#111827] mb-2';

const inputBase =
  'w-full h-[52px] sm:h-[56px] rounded-xl border bg-white px-4 sm:px-5 pr-12 ' +
  'text-[16px] sm:text-[17px] text-[#111827] placeholder:text-[#9CA3AF] ' +
  'outline-none transition';

const focusGreen =
  'focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/20';
const borderDefault = 'border-[#D1D5DB]';

function cn(...cls: Array<string | false | undefined | null>) {
  return cls.filter(Boolean).join(' ');
}

function EyeIcon({ open }: { open: boolean }) {
  return open ? (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3 3l18 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M10.58 10.58A3 3 0 0012 15a3 3 0 002.42-4.42"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M9.88 5.09A10.46 10.46 0 0112 5c7 0 10 7 10 7a18.2 18.2 0 01-4.33 5.06"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M6.61 6.61A18.24 18.24 0 002 12s3 7 10 7c1.03 0 2.02-.15 2.95-.43"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  ) : (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7S2 12 2 12z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M12 15a3 3 0 100-6 3 3 0 000 6z"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  );
}

function useOtp(length = 6) {
  const [digits, setDigits] = useState<string[]>(
    Array.from({ length }, () => ''),
  );
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const setDigit = (idx: number, val: string) => {
    setDigits((prev) => {
      const next = prev.slice();
      next[idx] = val;
      return next;
    });
  };

  const focusIdx = (idx: number) => {
    const el = inputsRef.current[idx];
    if (el) {
      el.focus();
      el.select();
    }
  };

  const onChange = (idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    if (raw.length <= 1) {
      const val = raw.replace(/\D/g, '');
      setDigit(idx, val);
      if (val && idx < digits.length - 1) focusIdx(idx + 1);
      return;
    }

    const paste = raw.replace(/\D/g, '').slice(0, digits.length - idx);
    if (!paste) return;

    setDigits((prev) => {
      const next = prev.slice();
      for (let i = 0; i < paste.length; i++) next[idx + i] = paste[i];
      return next;
    });

    const last = Math.min(idx + paste.length - 1, digits.length - 1);
    focusIdx(last < digits.length - 1 ? last + 1 : last);
  };

  const onKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') return;

    if (e.key === 'Backspace') {
      if (digits[idx]) {
        setDigit(idx, '');
        return;
      }
      if (idx > 0) {
        e.preventDefault();
        focusIdx(idx - 1);
        setDigit(idx - 1, '');
      }
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      e.preventDefault();
      focusIdx(idx - 1);
    } else if (e.key === 'ArrowRight' && idx < digits.length - 1) {
      e.preventDefault();
      focusIdx(idx + 1);
    }
  };

  const otp = useMemo(() => digits.join(''), [digits]);
  const reset = () => setDigits(Array.from({ length }, () => ''));

  return { digits, inputsRef, onChange, onKeyDown, otp, reset, focusIdx };
}

type StepRowProps = {
  index: number;
  label: string;
  desc: string;
  active?: boolean;
};

const StepRow: React.FC<StepRowProps> = ({ index, label, desc, active }) => (
  <div className="flex items-center gap-2">
    <div
      className={cn(
        'h-10 w-10 rounded-full border-2 flex items-center justify-center text-[14px] font-bold',
        active
          ? 'border-[#16A34A] bg-[#16A34A] text-white'
          : 'border-gray-300 text-gray-500',
      )}
    >
      {index}
    </div>
    <div className="flex-1">
      <Text
        className={cn(
          'block text-[11px] font-semibold',
          active ? 'text-gray-900' : 'text-gray-500',
        )}
      >
        {label}
      </Text>
      <Text type="secondary" className="block text-[11px]">
        {desc}
      </Text>
    </div>
  </div>
);

const parseErrors = (raw: any): { top?: string; fields?: FieldErrorMap } => {
  if (!raw) return {};
  if (typeof raw === 'string') return { top: raw };

  if (typeof raw === 'object') {
    const fields: FieldErrorMap = {};
    let top: string | undefined;

    for (const [k, v] of Object.entries(raw)) {
      if (Array.isArray(v)) fields[k] = v.map(String);
      else if (typeof v === 'string') {
        if (['error', 'detail', 'message', 'non_field_errors'].includes(k))
          top = v;
        else fields[k] = [v];
      }
    }

    if (!top && typeof (raw as any).error === 'string')
      top = (raw as any).error;
    return { top, fields };
  }

  return {};
};

const ResetPasswordFlow: React.FC = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();

  const [stage, setStage] = useState<Stage>('intro');
  const [topError, setTopError] = useState<string | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [idType, setIdType] = useState<string | null>(null);
  const [idName, setIdName] = useState<string>('Identification');

  const [idValue, setIdValue] = useState('');
  const [idError, setIdError] = useState<string | null>(null);

  const { digits, inputsRef, onChange, onKeyDown, otp, reset, focusIdx } =
    useOtp(6);

  const [pwd1, setPwd1] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [showPwd1, setShowPwd1] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const [pwdErrors, setPwdErrors] = useState<{
    token?: string;
    p1?: string;
    p2?: string;
  }>({});

  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (stage === 'confirmation') {
      focusIdx(0);
      setSecondsLeft(RESEND_COOLDOWN);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  useEffect(() => {
    if (stage !== 'confirmation' || secondsLeft <= 0) return;
    const id = window.setInterval(
      () => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)),
      1000,
    );
    return () => window.clearInterval(id);
  }, [stage, secondsLeft]);

  const fmt = (n: number) => {
    const m = Math.floor(n / 60);
    const s = n % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const stepIndex = stage === 'intro' ? 1 : stage === 'confirmation' ? 2 : 3;

  const onStartReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setTopError(null);
    setIdError(null);

    if (!idValue?.trim()) {
      setIdError('Please enter your identification value.');
      return;
    }

    setIsSubmitting(true);
    try {
      const introRes = await dispatch(
        postPasswordIntroduction(HARD_CODED_INTRO),
      );

      if (!postPasswordIntroduction.fulfilled.match(introRes)) {
        const rawErr =
          (introRes as any).payload ?? (introRes as any).error?.message;
        const { top } = parseErrors(rawErr);
        setTopError(
          getProfessionalErrorMessage(top || 'Failed to start password reset.'),
        );
        return;
      }

      const introData = (introRes as any).payload as any;
      const sess = introData?.session_id as string | undefined;
      const _idType = introData?.id as string | undefined;
      const _idName = introData?.name as string | undefined;

      if (!sess || !_idType) {
        setTopError('Failed to initialize reset session.');
        return;
      }

      setSessionId(sess);
      setIdType(_idType);
      setIdName(_idName || 'Identification');

      const valRes = await dispatch(
        postPasswordValidation({
          session_id: sess,
          id_type: _idType,
          value: idValue.trim(),
        }),
      );

      if (!postPasswordValidation.fulfilled.match(valRes)) {
        const rawErr =
          (valRes as any).payload ?? (valRes as any).error?.message;
        const { top, fields } =
          typeof rawErr === 'string'
            ? { top: undefined, fields: { value: [rawErr] } }
            : parseErrors(rawErr);

        if (fields?.value?.length)
          setIdError(getProfessionalErrorMessage(fields.value.join(' ')));
        setTopError(
          top
            ? getProfessionalErrorMessage(top)
            : !fields?.value
              ? 'Validation failed.'
              : null,
        );
        return;
      }

      reset();
      setStage('confirmation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onResend = async () => {
    if (secondsLeft > 0 || !sessionId || !idType || !idValue?.trim()) return;

    setResendLoading(true);
    setTopError(null);

    try {
      const res = await dispatch(
        postPasswordValidation({
          session_id: sessionId,
          id_type: idType,
          value: idValue.trim(),
        }),
      );

      if (!postPasswordValidation.fulfilled.match(res)) {
        const rawErr = (res as any).payload ?? (res as any).error?.message;
        const { top } = parseErrors(rawErr);
        setTopError(
          getProfessionalErrorMessage(
            top || 'Failed to resend code. Please try again.',
          ),
        );
        return;
      }

      setSecondsLeft(RESEND_COOLDOWN);
      reset();
      focusIdx(0);
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Failed to resend code. Please try again.';
      setTopError(getProfessionalErrorMessage(msg));
    } finally {
      setResendLoading(false);
    }
  };

  const onConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setTopError(null);
    setPwdErrors({});

    if (!sessionId) {
      setTopError('Session expired. Please start again.');
      setStage('intro');
      return;
    }

    const errs: { token?: string; p1?: string; p2?: string } = {};
    if (otp.length !== 6) errs.token = 'Enter the 6-digit code.';
    if (!pwd1) errs.p1 = 'Enter a strong password.';
    if (!pwd2) errs.p2 = 'Confirm your new password.';
    if (pwd1 && pwd2 && pwd1 !== pwd2)
      errs.p2 = "The two password fields didn't match.";

    if (Object.keys(errs).length) {
      setPwdErrors(errs);
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await dispatch(
        postPasswordConfirmation({
          session_id: sessionId,
          new_password1: pwd1,
          new_password2: pwd2,
          token: otp,
        }),
      );

      if (postPasswordConfirmation.fulfilled.match(res)) {
        setStage('success');
        return;
      }

      const rawErr = (res as any).payload ?? (res as any).error?.message;
      const { top, fields } = parseErrors(rawErr);

      if (fields?.token?.length) {
        setPwdErrors((prev) => ({
          ...prev,
          token: getProfessionalErrorMessage(fields.token.join(' ')),
        }));
      }
      if (fields?.new_password1?.length) {
        setPwdErrors((prev) => ({
          ...prev,
          p1: getProfessionalErrorMessage(fields.new_password1.join(' ')),
        }));
      }
      if (fields?.new_password2?.length) {
        setPwdErrors((prev) => ({
          ...prev,
          p2: getProfessionalErrorMessage(fields.new_password2.join(' ')),
        }));
      }

      setTopError(
        top
          ? getProfessionalErrorMessage(top)
          : !Object.keys(fields || {}).length
            ? 'Password reset failed.'
            : null,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderAlert = (error: string) => (
    <div>
      <div>{error}</div>
      {error.includes('unavailable') && (
        <div className="mt-2 text-xs">
          Need help? Contact{' '}
          <a
            href="mailto:support@nyandaruapay.gov.ke"
            className="underline text-blue-600 hover:text-blue-800"
          >
            support@nyandaruapay.gov.ke
          </a>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-white flex justify-center items-stretch px-4 md:px-6">
      <div className="w-full max-w-[1200px] flex flex-col md:flex-row bg-white">
        <div className="flex-1 flex flex-col justify-between items-center py-8 md:py-12 px-6 md:px-10 relative overflow-hidden bg-white shadow">
          <div className="relative z-10 w-full max-w-[480px] flex flex-col gap-6 flex-grow justify-center">
            <div className="mb-2 flex w-[280px] items-center justify-start">
              <Logo />
            </div>

            <div>
              <Title level={3} className="mb-1!">
                Forgot Password?
              </Title>
              <Text type="secondary">
                Reset your password in a few quick steps.
              </Text>
            </div>

            <div className="mt-2 mb-2 flex flex-col gap-4">
              <StepRow
                index={1}
                label="Verify identity"
                desc={`Enter your ${(idName || 'identification').toLowerCase()} number.`}
                active={stepIndex === 1}
              />
              <StepRow
                index={2}
                label="Enter OTP & New Password"
                desc="Use the 6-digit code and choose a new password."
                active={stepIndex === 2}
              />
              <StepRow
                index={3}
                label="Done"
                desc="Use your new password to sign in."
                active={stepIndex === 3}
              />
            </div>

            {topError && (
              <Alert
                type="error"
                showIcon
                className="mt-1"
                message={renderAlert(topError)}
              />
            )}

            {stage === 'intro' && (
              <form
                noValidate
                autoComplete="off"
                onSubmit={onStartReset}
                className="flex flex-col gap-5"
              >
                <div>
                  <label className={labelClass}>
                    {(idName || 'Identification') + ' Number'}
                  </label>

                  <input
                    autoFocus
                    value={idValue}
                    onChange={(e) => setIdValue(e.target.value)}
                    disabled={isSubmitting}
                    placeholder="e.g. 3393922"
                    className={cn(
                      inputBase,
                      focusGreen,
                      idError
                        ? 'border-[#DC2626] focus:border-[#DC2626] focus:ring-[#DC2626]/15'
                        : borderDefault,
                    )}
                  />

                  {idError && (
                    <div className="mt-1 text-[13px] text-red-600">
                      {idError}
                    </div>
                  )}
                </div>

                <ThemedButton
                  htmlType="submit"
                  theme="primary"
                  variant="solid"
                  text={isSubmitting ? 'Please wait...' : 'Send reset code'}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  className="h-11! w-full! rounded-md! text-[15px]! font-semibold!"
                />

                <div className="border-t border-gray-100 pt-4 mt-2 text-center text-[12px] text-gray-500 flex justify-center gap-1">
                  Remembered your password?{' '}
                  <Link
                    href="/account/signin/"
                    className="group flex items-center gap-1 text-[var(--color-accent)]!"
                  >
                    <span className="font-semibold group-hover:underline transition-all">
                      Log In
                    </span>
                    <span className="transition-transform translate-x-0 opacity-0 group-hover:translate-x-1 group-hover:opacity-100">
                      &rarr;
                    </span>
                  </Link>
                </div>
              </form>
            )}

            {stage === 'confirmation' && (
              <form
                noValidate
                onSubmit={onConfirmReset}
                className="flex flex-col gap-5"
              >
                <div>
                  <div className="mb-2 text-[14px] sm:text-[15px] font-medium text-[#111827]">
                    Type your 6 digit security code
                  </div>

                  <div className="flex items-center justify-between w-full gap-2 sm:gap-4">
                    {Array.from({ length: digits.length }).map((_, idx) => (
                      <div
                        key={idx}
                        className={cn(
                          'flex-1 max-w-[52px] sm:max-w-[62px]',
                          'h-[54px] sm:h-[60px] rounded-xl border-2 bg-white',
                          'flex items-center justify-center transition',
                          'border-[#16A34A]',
                        )}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          focusIdx(idx);
                        }}
                        onClick={() => focusIdx(idx)}
                      >
                        <input
                          ref={(el) => {
                            inputsRef.current[idx] = el;
                          }}
                          value={digits[idx] ?? ''}
                          onChange={(e) => onChange(idx, e)}
                          onKeyDown={(e) => onKeyDown(idx, e)}
                          maxLength={1}
                          inputMode="numeric"
                          aria-label={`OTP digit ${idx + 1}`}
                          autoComplete={idx === 0 ? 'one-time-code' : 'off'}
                          className="w-full h-full bg-transparent text-center text-[18px] sm:text-[20px] font-semibold text-[#111827] outline-none"
                        />
                      </div>
                    ))}
                  </div>

                  {pwdErrors.token && (
                    <div className="mt-2 text-center text-[13px] text-red-600">
                      {pwdErrors.token}
                    </div>
                  )}

                  <div className="mt-2 text-center text-[12px] text-gray-500">
                    {secondsLeft > 0
                      ? `Resend code in ${fmt(secondsLeft)}`
                      : 'You can resend the code now.'}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>New password</label>
                  <div className="relative">
                    <input
                      type={showPwd1 ? 'text' : 'password'}
                      value={pwd1}
                      onChange={(e) => setPwd1(e.target.value)}
                      disabled={isSubmitting}
                      placeholder="Enter your new password"
                      autoComplete="new-password"
                      className={cn(
                        inputBase,
                        focusGreen,
                        pwdErrors.p1
                          ? 'border-[#DC2626] focus:border-[#DC2626] focus:ring-[#DC2626]/15'
                          : borderDefault,
                      )}
                    />
                    <button
                      type="button"
                      aria-label={showPwd1 ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPwd1((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-9 w-9 items-center justify-center rounded-lg text-[#6B7280] hover:text-[#111827] hover:bg-[#F3F4F6] focus:outline-none focus:ring-2 focus:ring-[#16A34A]/25"
                    >
                      <EyeIcon open={showPwd1} />
                    </button>
                  </div>
                  {pwdErrors.p1 && (
                    <div className="mt-1 text-[13px] text-red-600">
                      {pwdErrors.p1}
                    </div>
                  )}
                </div>

                <div>
                  <label className={labelClass}>Confirm new password</label>
                  <div className="relative">
                    <input
                      type={showPwd2 ? 'text' : 'password'}
                      value={pwd2}
                      onChange={(e) => setPwd2(e.target.value)}
                      disabled={isSubmitting}
                      placeholder="Confirm your new password"
                      autoComplete="new-password"
                      className={cn(
                        inputBase,
                        focusGreen,
                        pwdErrors.p2
                          ? 'border-[#DC2626] focus:border-[#DC2626] focus:ring-[#DC2626]/15'
                          : borderDefault,
                      )}
                    />
                    <button
                      type="button"
                      aria-label={showPwd2 ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPwd2((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-9 w-9 items-center justify-center rounded-lg text-[#6B7280] hover:text-[#111827] hover:bg-[#F3F4F6] focus:outline-none focus:ring-2 focus:ring-[#16A34A]/25"
                    >
                      <EyeIcon open={showPwd2} />
                    </button>
                  </div>
                  {pwdErrors.p2 && (
                    <div className="mt-1 text-[13px] text-red-600">
                      {pwdErrors.p2}
                    </div>
                  )}
                </div>

                <div className="mt-1 flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-3">
                    <ThemedButton
                      theme="accent"
                      variant="outline"
                      text="Back"
                      iconPosition="prefix"
                      icon={<FiArrowLeft />}
                      onClick={() => {
                        setStage('intro');
                        setTopError(null);
                        setPwdErrors({});
                      }}
                      disabled={isSubmitting}
                      className="h-10! rounded-md! text-[13px]! font-semibold!"
                    />

                    <div className="flex items-center gap-2">
                      <ThemedButton
                        theme="accent"
                        variant="outline"
                        text="Clear code"
                        onClick={() => {
                          reset();
                          focusIdx(0);
                        }}
                        disabled={isSubmitting}
                        className="h-10! rounded-md! text-[13px]! font-semibold!"
                      />

                      <ThemedButton
                        theme="accent"
                        variant="outline"
                        text={
                          secondsLeft > 0
                            ? `Resend (${fmt(secondsLeft)})`
                            : resendLoading
                              ? 'Resending...'
                              : 'Resend code'
                        }
                        loading={resendLoading}
                        onClick={onResend}
                        disabled={
                          isSubmitting || resendLoading || secondsLeft > 0
                        }
                        className="h-10! rounded-md! text-[13px]! font-semibold!"
                      />
                    </div>
                  </div>

                  <ThemedButton
                    htmlType="submit"
                    theme="primary"
                    variant="solid"
                    text={isSubmitting ? 'Please wait...' : 'Reset password'}
                    loading={isSubmitting}
                    disabled={isSubmitting}
                    className="h-11! w-full! rounded-md! text-[15px]! font-semibold!"
                  />
                </div>

                <div className="border-t border-gray-100 pt-4 mt-2 text-center text-[12px] text-gray-500">
                  Need help?{' '}
                  <a
                    href="mailto:support@nyandaruapay.gov.ke"
                    className="underline text-blue-600 hover:text-blue-800"
                  >
                    Contact support
                  </a>
                </div>
              </form>
            )}

            {stage === 'success' && (
              <div className="flex flex-col gap-4">
                <Alert
                  type="success"
                  showIcon
                  message="Your password has been updated successfully."
                />
                <ThemedButton
                  theme="primary"
                  variant="solid"
                  text="Proceed to login"
                  onClick={() => router.push('/account/signin')}
                  className="h-11! w-full! rounded-md! text-[15px]! font-semibold!"
                />
              </div>
            )}
          </div>

          <div className="w-full flex justify-start">
            <AuthFooter />
          </div>
        </div>

        <div className="hidden bg-white lg:flex flex-shrink-0 items-stretch justify-center">
          <div className="relative w-[410px] max-w-full h-screen">
            <Image
              src="/images/auth-hero.webp"
              alt="Auth hero"
              fill
              style={{ objectFit: 'cover', objectPosition: 'top' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordFlow;
